import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface FxRevaluationItem {
  id: string;
  line_number: number;
  account_id: string;
  currency_id: string;
  subconto_1_id: string | null;
  subconto_1_type: string | null;
  currency_balance: string;
  old_uah_balance: string;
  new_rate: string;
  new_uah_balance: string;
  fx_diff: string;
  entry_kind: 'INCOME' | 'EXPENSE' | 'NONE';
  revaluation_date?: string | null;
}

export interface FxRevaluationListItem {
  id: string;
  organization_id: string;
  org_name?: string | null;
  document_number: string;
  document_date: string;
  revaluation_date: string;
  period_start?: string | null;
  period_end?: string | null;
  total_income: string;
  total_expense: string;
  posted: boolean;
  deletion_mark: boolean;
  comment?: string | null;
  // Block 5.3f:
  state?: CancellableState;
}

export interface FxRevaluation extends FxRevaluationListItem {
  posted_at: string | null;
  comment: string | null;
  items: FxRevaluationItem[];
  warnings?: string[];   // ч.800: проводки без валютної суми (recalc)
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface FxRevaluationCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  revaluation_date: string;
  period_start?: string | null;
  period_end?: string | null;
  comment?: string | null;
}

export type FxRevaluationUpdate = Partial<FxRevaluationCreate>;

export interface FxPreviewItem {
  line_number: number;
  account_id: string;
  account_code: string;
  analytics: string | null;
  currency_id: string;
  currency_code: string;
  currency_balance: string;
  old_uah_balance: string;
  new_rate: string;
  new_uah_balance: string;
  fx_diff: string;
  entry_kind: 'INCOME' | 'EXPENSE' | 'NONE';
  revaluation_date: string;
}

export const fxRevaluations = {
  // Прев'ю довідки БЕЗ збереження (0441-2): «сформувати» на /new.
  preview: (payload: { organization_id: string; period_start: string; period_end: string }) =>
    api.post<{ items: FxPreviewItem[]; warnings?: string[] }>('/fx-revaluations/preview', payload).then((r) => r.data),
  list: (params?: {
    organization_id?: string; posted?: boolean;
    date_from?: string; date_to?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
    limit?: number; offset?: number;
  }) =>
    api.get<Page<FxRevaluationListItem>>('/fx-revaluations', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<FxRevaluation>(`/fx-revaluations/${id}`).then((r) => r.data),
  create: (payload: FxRevaluationCreate) =>
    api.post<FxRevaluation>('/fx-revaluations', payload).then((r) => r.data),
  update: (id: string, payload: FxRevaluationUpdate) =>
    api.patch<FxRevaluation>(`/fx-revaluations/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/fx-revaluations/${id}`).then(() => undefined),
  recalc: (id: string) =>
    api.post<FxRevaluation>(`/fx-revaluations/${id}/recalc`).then((r) => r.data),
  post: (id: string) =>
    api.post<FxRevaluation>(`/fx-revaluations/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<FxRevaluation>(`/fx-revaluations/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/fx-revaluations/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
